export type SimpleClause = TermQuery | TermsQuery | RangeQuery;

export type NestedQuery = BoolSubQuery | SimpleClause;

export type Query = NestedQuery | BoolQuery;

export type BoolSubQuery = {
  bool: BoolQuery;
};

export type TermValue = QueryValue;

export type TermQuery = {
  term: {
    [field: string]: TermValue;
  };
};

export type TermsValue = QueryValue[];
export type TermsQuery = {
  terms: {
    [field: string]: TermsValue;
  };
};

export type RangeValue = {
  gte?: QueryValue;
  gt?: QueryValue;
  lte?: QueryValue;
  lt?: QueryValue;
};
export type RangeQuery = {
  range: {
    [field: string]: RangeValue;
  };
};

export type SimpleClauseValue = TermValue | TermsValue | RangeValue;

export type BoolQuery<T = NestedQuery> = {
  must?: T[];
  must_not?: T[];
  should?: T[];
};

export type SimpleBoolQuery = BoolQuery<SimpleClause>;

export function isBoolQuery(query: Query): query is BoolQuery {
  return (
    typeof query === 'object' &&
    ['must', 'must_not', 'should'].some((x) => x in query && Array.isArray((query as never)[x]))
  );
}

export function isSimpleBoolQuery(query: Query): query is SimpleBoolQuery {
  return isBoolQuery(query) &&
    (!query.must?.length || query.must?.every((x) => isSimpleClause(x))) &&
    (!query.must_not?.length || query.must_not?.every((x) => isSimpleClause(x))) &&
    (!query.should?.length || query.should?.every((x) => isSimpleClause(x)))
    ? true
    : false;
}

export function isBoolSubQuery(query: Query): query is BoolSubQuery {
  return typeof query === 'object' && 'bool' in query && isBoolQuery((query as BoolSubQuery).bool);
}

export function isTermQuery(query: Query): query is TermQuery {
  return (
    typeof query === 'object' &&
    Object.keys(query).length === 1 &&
    Object.keys(query)[0] === 'term' &&
    Object.values(query).length === 1 &&
    !Array.isArray(Object.values(Object.values(query)[0])[0])
  );
}

export function isTermsQuery(query: Query): query is TermsQuery {
  return (
    typeof query === 'object' &&
    Object.keys(query).length === 1 &&
    Object.keys(query)[0] === 'terms' &&
    Object.values(query).length === 1 &&
    Array.isArray(Object.values(Object.values(query)[0])[0])
  );
}

export function isRangeQuery(query: Query): query is RangeQuery {
  return (
    typeof query === 'object' &&
    Object.keys(query).length === 1 &&
    Object.keys(query)[0] === 'range' &&
    Object.values(query).length === 1 &&
    typeof Object.values(query)[0] === 'object' &&
    ['gte', 'gt', 'lte', 'lt'].some((x) => x in (Object.values(Object.values(query)[0])[0] as Record<string, unknown>))
  );
}

export function isSimpleClause(query: Query): query is SimpleClause {
  return isTermQuery(query) || isTermsQuery(query) || isRangeQuery(query) ? true : false;
}

export type QueryValue = number | string | boolean;
